import React, { useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { InsertGroupDialog } from "../components/groups/InsertGroupDialog";
import {
  DeleteIcon,
  EditIcon,
  GroupIcon,
  SortAscendingDirectionIcon,
  SortDescendingDirectionIcon,
} from "../components/icons";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import SearchInput from "../components/utils/SearchInput";
import {
  Button,
  ButtonGroup,
  Container,
  EmptyState,
  H6,
  Link,
  ScreenContainer,
  ScreenHeader,
} from "../components/wombat-ui";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import { Tag } from "@chakra-ui/react";
import { getGroupColor } from "../utils/colors";
import useConfirm from "../components/utils/confirm/useConfirm";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import {
  GroupFragment,
  useDeleteGroup,
  useGetGroups,
} from "../graphql/generated/definitions";

type GroupModalState = {
  open: boolean;
  selectedGroup?: GroupFragment;
};
type GroupListProps = RouteComponentProps<{ id: string }>;

type GroupItemProps = {
  deleteGroup: ReturnType<typeof useDeleteGroup>[0];
  setGroupModal: (state: GroupModalState) => void;
  group: GroupFragment;
};

const GroupItem: React.FC<GroupItemProps> = ({
  group,
  deleteGroup,
  setGroupModal,
}) => {
  const confirm = useConfirm();
  const t = useTranslations();

  return (
    <div
      id={`group_${group.id}`}
      className="grid items-center grid-cols-5 py-4 pt-2 sm:grid-cols-12"
    >
      <div className="col-span-3 sm:col-span-3">
        <Link
          to={`/users?groupId=${group.id}`}
          id={`${group.name}-link`}
          className="text-sm font-medium"
        >
          <p className="text-sm font-medium">{group.name}</p>
        </Link>
      </div>
      <div className="hidden sm:block sm:col-span-6">
        <p className="text-xs">{group.description}</p>
      </div>
      <div className="flex items-center col-span-1 sm:col-span-2">
        <Tag colorScheme={getGroupColor(group.kind)}>
          {t(`groups.types.${group.kind}` as const)}
        </Tag>
      </div>
      <div className="flex justify-end col-span-1">
        {group.canInsert && (
          <Button
            id={`group_edit_${group.id}`}
            variant="ghost"
            onClick={() =>
              setGroupModal({ open: true, selectedGroup: { ...group } })
            }
          >
            <EditIcon className="w-5 h-5" />
          </Button>
        )}
        {group.canDestroy && (
          <Button
            variant="ghost"
            id={`group_delete_${group.id}`}
            onClick={() => {
              confirm({
                title: t("groups.delete"),
                message: t("groups.confirmDelete"),
                buttons: [
                  { text: t("forms.actions.cancel") },
                  {
                    colorScheme: "red",
                    id: "delete-group",
                    text: t("forms.actions.delete"),
                    onClick: async () =>
                      deleteGroup({
                        variables: { input: { id: group.id } },
                      }),
                  },
                ],
              });
            }}
          >
            <DeleteIcon className="w-5 h-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

const GroupList: React.FC<GroupListProps> = () => {
  const t = useTranslations();
  const { isCapableOfInsertingGroups, isCapableOfReadingGroups } = useViewer();

  useBreadcrumbs([
    {
      to: "/groups",
      text: t("groups.list") as string,
    },
  ]);

  const [groupModal, setGroupModal] = useState<GroupModalState>({
    open: false,
    selectedGroup: undefined,
  });

  const [{ page, q, sortBy, direction }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: StringParam,
    q: StringParam,
    groupId: StringParam,
  });

  const { data, loading, refetch } = useGetGroups({
    variables: {
      page: page || 1,
      per: 20,
      q: q || undefined,
      sortBy: sortBy || "name",
      direction: direction as "ASC" | "DESC",
    },
  });
  const groups = data?.groups?.collection || [];
  const itemsCount = data?.groups?.metadata?.totalCount;

  const [deleteGroup] = useDeleteGroup({
    onCompleted: refetch,
  });

  const actions = useMemo(
    () => [
      isCapableOfInsertingGroups && (
        <Button
          key="groupInsertButton"
          id="groupInsertButton"
          onClick={() =>
            setGroupModal({ open: true, selectedGroup: undefined })
          }
        >
          {t("groups.add")}
        </Button>
      ),
    ],
    []
  );

  const [search, setSearch] = useState(q);

  return (
    <ScreenContainer
      error={
        !isCapableOfReadingGroups ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        title={
          <span className="flex items-center space-x-4">
            {t("groups.list")}
          </span>
        }
        actions={actions}
        description={t("groups.listDescription")}
        icon={GroupIcon}
      />
      <Container>
        <div className="flex flex-col py-5">
          <div className="flex flex-col py-4 space-y-4">
            <>
              <List
                isLoading={loading}
                emptyMessage={t("groups.noMatchingGroups") as string}
                data={groups}
                listOnly
                renderAsList={(data) => (
                  <GroupItem
                    group={data}
                    setGroupModal={setGroupModal}
                    deleteGroup={deleteGroup}
                  />
                )}
                id="users-list"
              >
                <SearchInput
                  onSubmit={(q) => setQuery({ page: 1, q })}
                  label={t("list.search") as string}
                  placeholder={t("groups.searchPlaceholder") as string}
                  value={search}
                  onChange={setSearch}
                />
                <div className="flex space-x-3">
                  <ButtonGroup scale="sm">
                    <Button
                      icon={SortAscendingDirectionIcon}
                      onClick={() => setQuery({ direction: "ASC" })}
                      variant={
                        direction === "ASC" || !direction
                          ? "primary"
                          : undefined
                      }
                    />
                    <Button
                      icon={SortDescendingDirectionIcon}
                      onClick={() => setQuery({ direction: "DESC" })}
                      variant={direction === "DESC" ? "primary" : undefined}
                    />
                  </ButtonGroup>
                </div>
              </List>
              {!loading && (
                <Pagination
                  page={page}
                  itemsCount={itemsCount}
                  itemsPerPage={20}
                  onChange={(page) => setQuery({ page })}
                />
              )}
            </>
          </div>
        </div>
      </Container>
      <InsertGroupDialog
        open={groupModal.open}
        group={groupModal.selectedGroup}
        onClose={() => setGroupModal({ open: false, selectedGroup: undefined })}
      />
    </ScreenContainer>
  );
};

export default React.memo(GroupList);
